import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Thread } from './chat/Thread';
import { useCustomLangGraphRuntime } from '../services/langGraphRuntime';
import { shortid } from '../services/id.service';
import { ConversationHistory } from './chat/ConversationHistory';

interface EllieProps {
  initialQuestion?: string;
  showHistory?: boolean;
  conversationId?: string;
  agentType?: 'analyst' | 'use_case_discovery' | 'eda' | 'data_modeling' | 'ellie';
  sourceId?: string;
  dataSourceId?: string;
  useCaseId?: string;
  onValidationComplete?: () => void;
}

// Wrapper component that renders the assistant with a specific thread ID
const AssistantWrapper = ({
  threadId,
  initialQuestion,
  onAnalysisGenerated,
  agentType = 'analyst',
  sourceId,
  dataSourceId,
  useCaseId,
  onValidationComplete,
}: {
  threadId: string;
  initialQuestion: string;
  onAnalysisGenerated: (analysisId: string) => void;
  agentType?: 'analyst' | 'use_case_discovery' | 'eda' | 'data_modeling' | 'ellie';
  sourceId?: string;
  dataSourceId?: string;
  useCaseId?: string;
  onValidationComplete?: () => void;
}) => {
  const { runtime, isHistoricalThread, isLoaded, compactConversation, messageCount } = useCustomLangGraphRuntime({
    agentType,
    model: 'claude-4-sonnet',
    threadId,
    sourceId,
    dataSourceId,
    useCaseId,
  });

  // Auto-run charts for new threads only
  // Historical threads should start with charts collapsed to avoid loading all charts at once
  const autoRunEnabled = !isHistoricalThread;

  // Show loading state while loading historical thread
  if (isHistoricalThread && !isLoaded) {
    return (
      <div className="p-4">
        <p>Loading conversation...</p>
      </div>
    );
  }

  return (
    <Thread
      runtime={runtime}
      initialQuestion={initialQuestion}
      toolCallHandlers={{
        onAnalysisGenerated,
      }}
      autoRunCharts={autoRunEnabled}
      onValidationComplete={onValidationComplete}
      onCompact={compactConversation}
      messageCount={messageCount}
    />
  );
};

export const Ellie: React.FC<EllieProps> = ({
  initialQuestion = '',
  showHistory = false,
  conversationId,
  agentType = 'analyst',
  sourceId,
  dataSourceId,
  useCaseId,
  onValidationComplete,
}) => {
  // Use conversationId if provided, otherwise generate a new thread ID
  const [defaultThreadId] = useState(() => shortid());
  const currentThreadId = conversationId || defaultThreadId;
  const navigate = useNavigate();

  const [analysisIds, setAnalysisIds] = useState<string[]>([]);

  const handleNewChat = useCallback(() => {
    navigate(`/ellie/conversation/${shortid()}`);
  }, [navigate]);

  const content = (
    <AssistantWrapper
      key={currentThreadId}
      threadId={currentThreadId}
      initialQuestion={initialQuestion}
      agentType={agentType}
      sourceId={sourceId}
      dataSourceId={dataSourceId}
      useCaseId={useCaseId}
      onValidationComplete={onValidationComplete}
      onAnalysisGenerated={(analysisId) => {
        if (analysisId && !analysisIds.includes(analysisId)) {
          setAnalysisIds([...analysisIds, analysisId]);
        }
      }}
    />
  );

  if (showHistory) {
    return (
      <div className="h-full w-full flex">
        <div className="flex-[3] min-w-0">
          {content}
        </div>
        <div className="flex-1 min-w-0">
          <ConversationHistory activeThreadId={currentThreadId} onNewChat={handleNewChat} />
        </div>
      </div>
    );
  }

  return (
    <div className="h-full w-full">
      {content}
    </div>
  );
};
